// Core packages
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";

// Third party packages
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { finalize, take } from "rxjs/operators";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { ConfirmDialogComponent } from 'src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component';
import { IUser } from "src/app/shared/models/user/user.interface";
import { ApiService } from "src/app/shared/services/api.service";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";
import { TankService } from '../../tank.service';

/**
 * Script start
 */

@Component({
  selector: "app-edit-tank-document-dialog",
  templateUrl: "./edit-tank-document-dialog.component.html",
  styleUrls: ["./edit-tank-document-dialog.component.scss"]
})
export class EditTankDocumentDialogComponent
  implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  dialogIsLoading = false; // Used to view/hide form loading spinner

  // BEGIN - Edit item form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly
  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.save"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true
    }
  };

  deleteBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant('common.cta.delete'),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: 'warn',
    spinnerColor: 'warn',
    fullWidth: false,
    disabled: false,
    mode: 'indeterminate',
    buttonIcon: {
      fontSet: 'fal',
      fontIcon: 'fa-trash',
      inline: true,
    },
  };
  // END - Edit item form

  docUploaded = false;
  file: any;
  tankDocumentId: string;
  roleType: string;

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<EditTankDocumentDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialog: MatDialog,
    private configService: ConfigService,
    private translateService: TranslateService,
    private authService: AuthService,
    private cookieService: CookieService,
    private toastrService: ToastrService,
    private helperService: HelperService,
    private tankService: TankService,
    private apiService: ApiService, // DO NOT MOVE since it's used for 'autocomplete' field type
  ) { }

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe(res => (this.loggedUser = res))
    );
    this.subscriptions.push(
      this.authService.loggedUserRequestedRoleType$.subscribe(
        res => (this.roleType = res)
      )
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        res => (this.loggedUserPermissions = res)
      )
    );

    // Get current lang
    this.lang = this.cookieService.get("lang") || this.configService.defaultLang;

    if (
      this.data.document
      && this.data.document._id
      && this.data.document.documentFile
      && this.data.document.documentFile[0]
    ) {
      this.file = this.data.document.documentFile[0];
      this.tankDocumentId = this.data.document._id;

      if ((this.roleType === 'admin'
        || this.roleType === 'operator')
        && !this.data.document.viewed
      ) {
        // Set viewed status to true
        this.subscriptions.push(
          this.tankService.updateDocStatus(this.tankDocumentId, true)
            .pipe(finalize(() => (this.formBtnOptions.active = false)))
            .subscribe()
        );
      }
    }

    this.initForm();
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(): void {
    this.fields = [];
    this.model = {
      name: this.data.document.name,
      condo: this.data.document.condo,
      description: this.data.document.description,
      documents: this.data.document.documentFile
    };

    if (this.form && this.form.controls) {
      for (const key in this.form.controls) {
        delete this.form.controls[key];
      }
    }
    this.fields = [
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12",
            type: "file-downloader",
            key: "documents",
            hideExpression: (model): boolean => !model || model.length === 0,
            templateOptions: {
              label: this.translateService.instant('properties.fields.documents'),
              required: false,
              bindLabel: "originalName",
              readonly: true,
            }
          },
          {
            className: "col-12",
            type: "input",
            key: "condo",
            templateOptions: {
              label: this.translateService.instant('tank.fields.condo'),
              placeholder: this.translateService.instant('tank.fields.condo'),
              minLength: 1,
              maxLength: 100,
            },
          },
          {
            className: "col-12",
            type: "input",
            key: "name",
            templateOptions: {
              label: this.translateService.instant('tank.fields.name'),
              placeholder: this.translateService.instant('tank.fields.name'),
              minLength: 1,
              maxLength: 100,
            },
          },
          {
            className: "col-12",
            type: "textarea",
            key: "description",
            templateOptions: {
              label: this.translateService.instant("tank.fields.description"),
              placeholder: this.translateService.instant("tank.fields.description"),
              required: false,
              minLength: 2,
              maxLength: 100,
              rows: 5,
            }
          },
        ]
      }
    ];
  }

  /**
   * Handle dialog closing
   *
   * @since 1.0.0
   */
  onClose(data?: any): void {
    // if (this.data) {
    //   if (this.data.item) {
    //     this.data.item.note = this.existingNotes;
    //   }
    // }
    this.dialogRef.close(data);
  }

  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
  onFormSubmit(): void {
    // Prevent invalid data submit
    // if (this.model && !this.model.name) {
    //   this.toastrService.error("Devi scegliere un nome per il file prima di procedere");
    //   return;
    // }
    // if (this.model && (!this.model.documentFile || (this.model.documentFile && !this.model.documentFile.length))) {
    //   this.toastrService.error("Devi caricare un file prima di procedere");
    //   return;
    // }

    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        this.form.controls[key].markAsTouched();
      });
      console.warn("form", this.form);
      this.toastrService.error(
        this.translateService.instant("common.errors.form_is_invalid")
      );
      return;
    }

    // Activate submit button loading status
    this.formBtnOptions.active = true;

    this.subscriptions.push(
      this.tankService.updateDoc(this.tankDocumentId, this.model)
        .pipe(finalize(() => (this.formBtnOptions.active = false)))
        .subscribe(
          res => {
            if (res.status) {
              this.dialogRef.close({ save: true });
            }
          },
          err => {
            this.helperService.handleFormlyError(this.form, err);
          }
        )
    );
  }


  /**
   * Remove current user from database
   *
   * @since 1.0.0
   */
  onDelete(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('dialogs.confirm.title'),
        message: this.translateService.instant('dialogs.confirm.message'),
        btnOkText: this.translateService.instant('common.cta.confirm'),
        btnCancelText: this.translateService.instant('common.cta.undo'),
      },
      width: '500px',
    });
    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    dialogRef.beforeClosed()
      .pipe(
        take(1)
      )
      .subscribe(
        res => {
          if (!res) {
            return;
          }
          this.deleteBtnOptions.active = true;
          this.subscriptions.push(
            this.tankService.delete(this.tankDocumentId)
              .pipe(
                finalize(() => this.deleteBtnOptions.active = false)
              )
              .subscribe(
                res2 => {
                  if (res2.status) {
                    // this.toastrService.success(res2.message);
                    this.dialogRef.close({ deleted: true });
                    return;
                  }
                },
                err => {
                  // Choose one of the following error handling
                  // method. The first one shows a message right
                  // under the form fields (if the form is properly
                  // setted), the second one shows toastr
                  // notifications for each error
                  // this.helperService.handleFormError(this.passwordForm, err);
                  this.helperService.handleError(err);
                }
              )
          );
        }
      );
  }
}
